import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const OrderBookTable = ({ selectedContract, priceGrouping, showAggregated }) => {
  const [orderBookData, setOrderBookData] = useState({
    bids: [],
    asks: [],
    lastUpdate: new Date()
  });
  const [recentChanges, setRecentChanges] = useState(new Set());

  // Mock order book data
  const mockOrderBookData = {
    'EUA-DEC24': {
      bids: [
        { price: 85.45, quantity: 2500, orders: 12, total: 2500 },
        { price: 85.40, quantity: 1800, orders: 8, total: 4300 },
        { price: 85.35, quantity: 3200, orders: 15, total: 7500 },
        { price: 85.30, quantity: 1500, orders: 6, total: 9000 },
        { price: 85.25, quantity: 2800, orders: 11, total: 11800 },
        { price: 85.20, quantity: 1200, orders: 5, total: 13000 },
        { price: 85.15, quantity: 2100, orders: 9, total: 15100 },
        { price: 85.10, quantity: 1900, orders: 7, total: 17000 },
        { price: 85.05, quantity: 2600, orders: 13, total: 19600 },
        { price: 85.00, quantity: 3400, orders: 18, total: 23000 }
      ],
      asks: [
        { price: 85.50, quantity: 1800, orders: 9, total: 1800 },
        { price: 85.55, quantity: 2200, orders: 11, total: 4000 },
        { price: 85.60, quantity: 1600, orders: 7, total: 5600 },
        { price: 85.65, quantity: 2900, orders: 14, total: 8500 },
        { price: 85.70, quantity: 1400, orders: 6, total: 9900 },
        { price: 85.75, quantity: 2500, orders: 12, total: 12400 },
        { price: 85.80, quantity: 1700, orders: 8, total: 14100 },
        { price: 85.85, quantity: 2300, orders: 10, total: 16400 },
        { price: 85.90, quantity: 1900, orders: 9, total: 18300 },
        { price: 85.95, quantity: 2700, orders: 13, total: 21000 }
      ]
    },
    'OIL-JAN25': {
      bids: [
        { price: 78.25, quantity: 5000, orders: 25, total: 5000 },
        { price: 78.20, quantity: 3800, orders: 18, total: 8800 },
        { price: 78.15, quantity: 4200, orders: 21, total: 13000 },
        { price: 78.10, quantity: 2900, orders: 14, total: 15900 },
        { price: 78.05, quantity: 3600, orders: 17, total: 19500 },
        { price: 78.00, quantity: 2400, orders: 12, total: 21900 },
        { price: 77.95, quantity: 4100, orders: 20, total: 26000 },
        { price: 77.90, quantity: 3300, orders: 16, total: 29300 },
        { price: 77.85, quantity: 2800, orders: 13, total: 32100 },
        { price: 77.80, quantity: 3900, orders: 19, total: 36000 }
      ],
      asks: [
        { price: 78.30, quantity: 3200, orders: 16, total: 3200 },
        { price: 78.35, quantity: 4100, orders: 20, total: 7300 },
        { price: 78.40, quantity: 2800, orders: 14, total: 10100 },
        { price: 78.45, quantity: 3700, orders: 18, total: 13800 },
        { price: 78.50, quantity: 2500, orders: 12, total: 16300 },
        { price: 78.55, quantity: 4300, orders: 21, total: 20600 },
        { price: 78.60, quantity: 3100, orders: 15, total: 23700 },
        { price: 78.65, quantity: 3800, orders: 19, total: 27500 },
        { price: 78.70, quantity: 2900, orders: 14, total: 30400 },
        { price: 78.75, quantity: 4200, orders: 20, total: 34600 }
      ]
    }
  };

  useEffect(() => {
    const updateOrderBook = () => {
      const baseData = mockOrderBookData?.[selectedContract] || mockOrderBookData?.['EUA-DEC24'];
      
      // Simulate real-time updates with small price variations
      const updatedBids = baseData?.bids?.map(bid => ({
        ...bid,
        quantity: Math.max(100, bid?.quantity + Math.floor(Math.random() * 400 - 200)),
        orders: Math.max(1, bid?.orders + Math.floor(Math.random() * 6 - 3))
      }));

      const updatedAsks = baseData?.asks?.map(ask => ({
        ...ask,
        quantity: Math.max(100, ask?.quantity + Math.floor(Math.random() * 400 - 200)),
        orders: Math.max(1, ask?.orders + Math.floor(Math.random() * 6 - 3))
      }));

      // Calculate cumulative totals
      let bidTotal = 0;
      const bidsWithTotal = updatedBids?.map(bid => {
        bidTotal += bid?.quantity;
        return { ...bid, total: bidTotal };
      });

      let askTotal = 0;
      const asksWithTotal = updatedAsks?.map(ask => {
        askTotal += ask?.quantity;
        return { ...ask, total: askTotal };
      });

      setOrderBookData({
        bids: bidsWithTotal,
        asks: asksWithTotal,
        lastUpdate: new Date()
      });

      // Highlight recent changes
      const changes = new Set();
      if (Math.random() > 0.7) {
        const randomIndex = Math.floor(Math.random() * 10);
        changes?.add(`bid-${randomIndex}`);
      }
      if (Math.random() > 0.7) {
        const randomIndex = Math.floor(Math.random() * 10);
        changes?.add(`ask-${randomIndex}`);
      }
      setRecentChanges(changes);

      // Clear highlights after animation
      setTimeout(() => setRecentChanges(new Set()), 1000);
    };

    updateOrderBook();
    const interval = setInterval(updateOrderBook, 2000);
    return () => clearInterval(interval);
  }, [selectedContract]);

  const formatPrice = (price) => {
    return selectedContract?.includes('EUA') ? `€${price?.toFixed(2)}` : `$${price?.toFixed(2)}`;
  };

  const formatQuantity = (quantity) => {
    return quantity?.toLocaleString();
  };

  const getMaxQuantity = () => {
    const allQuantities = [...orderBookData?.bids, ...orderBookData?.asks]?.map(item => item?.quantity);
    return Math.max(...allQuantities, 1);
  };

  const maxQuantity = getMaxQuantity();

  const renderOrderRow = (order, index, type) => {
    const isRecent = recentChanges?.has(`${type}-${index}`);
    const barWidth = (order?.quantity / maxQuantity) * 100;
    
    return (
      <tr
        key={`${type}-${index}`}
        className={`
          border-b border-border/50 hover:bg-muted/30 transition-all duration-200
          ${isRecent ? 'bg-accent/20 animate-pulse' : ''}
        `}
      >
        <td className="px-3 py-2 text-sm font-mono text-right relative">
          <div
            className={`
              absolute inset-y-0 right-0 opacity-20 transition-all duration-300
              ${type === 'bid' ? 'bg-success' : 'bg-error'}
            `}
            style={{ width: `${barWidth}%` }}
          />
          <span className={`relative z-10 ${type === 'bid' ? 'text-success' : 'text-error'} font-semibold`}>
            {formatPrice(order?.price)}
          </span>
        </td>
        <td className="px-3 py-2 text-sm font-mono text-right text-foreground">
          {formatQuantity(order?.quantity)}
        </td>
        {!showAggregated && (
          <td className="px-3 py-2 text-sm font-mono text-right text-muted-foreground">
            {order?.orders}
          </td>
        )}
        <td className="px-3 py-2 text-sm font-mono text-right text-muted-foreground">
          {formatQuantity(order?.total)}
        </td>
      </tr>
    );
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-subtle">
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-foreground">Order Book</h3>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Icon name="Clock" size={14} />
            <span>Last update: {orderBookData?.lastUpdate?.toLocaleTimeString()}</span>
          </div>
        </div>
      </div>
      <div className="overflow-hidden">
        {/* Asks (Sell Orders) */}
        <div className="max-h-64 overflow-y-auto">
          <table className="w-full">
            <thead className="bg-muted/50 sticky top-0">
              <tr>
                <th className="px-3 py-2 text-xs font-medium text-muted-foreground text-right">Price</th>
                <th className="px-3 py-2 text-xs font-medium text-muted-foreground text-right">Size</th>
                {!showAggregated && (
                  <th className="px-3 py-2 text-xs font-medium text-muted-foreground text-right">Orders</th>
                )}
                <th className="px-3 py-2 text-xs font-medium text-muted-foreground text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {orderBookData?.asks?.slice()?.reverse()?.map((ask, index) => 
                renderOrderRow(ask, orderBookData?.asks?.length - 1 - index, 'ask')
              )}
            </tbody>
          </table>
        </div>

        {/* Spread */}
        <div className="bg-muted/30 px-4 py-3 border-y border-border">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-foreground">Spread</span>
            <div className="flex items-center gap-4">
              <span className="text-sm font-mono text-muted-foreground">
                {orderBookData?.asks?.length > 0 && orderBookData?.bids?.length > 0 
                  ? formatPrice(orderBookData?.asks?.[0]?.price - orderBookData?.bids?.[0]?.price)
                  : '—'
                }
              </span>
              <span className="text-xs text-muted-foreground">
                ({orderBookData?.asks?.length > 0 && orderBookData?.bids?.length > 0 
                  ? (((orderBookData?.asks?.[0]?.price - orderBookData?.bids?.[0]?.price) / orderBookData?.bids?.[0]?.price) * 100)?.toFixed(3)
                  : '0.000'
                }%)
              </span>
            </div>
          </div>
        </div>

        {/* Bids (Buy Orders) */}
        <div className="max-h-64 overflow-y-auto">
          <table className="w-full">
            <thead className="bg-muted/50 sticky top-0">
              <tr>
                <th className="px-3 py-2 text-xs font-medium text-muted-foreground text-right">Price</th>
                <th className="px-3 py-2 text-xs font-medium text-muted-foreground text-right">Size</th>
                {!showAggregated && (
                  <th className="px-3 py-2 text-xs font-medium text-muted-foreground text-right">Orders</th>
                )}
                <th className="px-3 py-2 text-xs font-medium text-muted-foreground text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {orderBookData?.bids?.map((bid, index) => 
                renderOrderRow(bid, index, 'bid')
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default OrderBookTable;